import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import * as yup from 'yup';
import {ButtonBase, DialogContent} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import {useFormik} from "formik";
import Dialog from "@mui/material/Dialog";
import MyGoogleIcon from "../../assets/MyGoogleIcon";
import {LibrettoPlan} from "../../utils/utils";
import LoginForm from "../Landing/Components/LoginForm";

const validationSchema = yup.object({
    email: yup
        .string()
        .trim()
        .email('Please enter a valid email address')
        .required('Email is required.'),
    password: yup
        .string()
        .required('Please specify your password')
        .min(8, 'The password should have at minimum length of 8'),
});

const resetEmailValidationSchema = yup.object({
    email: yup
        .string()
        .trim()
        .email('Please enter a valid email address')
        .required('Email is required.'),
});

const V2LoginForm = ({
                         signinWithGoogle,
                         handleLoginFormClose,
                         signinWithEmail,
                         handleSignUpFormOpen,
                         sendPasswordResetEmailForUser,
                     }) => {
    const theme = useTheme();
    const { mode } = theme.palette;

    const [forgotPasswordMessageOpen, setForgotPasswordMessageOpen] = React.useState(false);
    const [resetEmailSent, setResetEmailSent] = React.useState(false);
    const [resetEmailError, setResetEmailError] = React.useState(false);

    const initialValues = {
        email: '',
        password: '',
    };

    const onSubmit = (values) => {
        signinWithEmail({ email: values.email, password: values.password });
    };

    const onResetEmailSubmit = (values) => {
        sendPasswordResetEmailForUser({
            email: values.email,
            setResetEmailSent,
            setResetEmailError,
        });
    };

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema,
        onSubmit,
    });

    const resetEmailFormik = useFormik({
        initialValues: { email: '' },
        validationSchema: resetEmailValidationSchema,
        onSubmit: onResetEmailSubmit,
    });

    const handleJoinWaitlistClick = () => {
        // Same behavior as before, just renamed for clarity in the UI.
        handleLoginFormClose();
        handleSignUpFormOpen({ LibrettoPlan: LibrettoPlan.StudioFree, code: '' });
    };

    const handleForgotPasswordMessageClose = () => {
        handleLoginFormClose();
        setForgotPasswordMessageOpen(false);
        setResetEmailSent(false);
        setResetEmailError(false);
    };

    const handleForgotPasswordClick = () => {
        setForgotPasswordMessageOpen(true);
    };

    const buttonCommonSx = {
        backgroundColor: '#000000',
        color: '#ffffff',
        height: 44,
        borderRadius: '12px',
        textTransform: 'none',
        fontFamily: '"Inter", sans-serif',
        fontWeight: 500,
        fontSize: '15px',
        boxShadow: mode === 'light' ? theme.shadows[1] : 'none',
        '&:hover': {
            backgroundColor: '#111111',
            boxShadow: mode === 'light' ? theme.shadows[2] : 'none',
        },
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 350,
                mx: 'auto',
                px: { xs: 1, sm: 0 }, // mobile side padding
                py: { xs: 2, sm: 0 }, // mobile vertical breathing room
            }}
        >
            {/* Logo Section */}
            <Box sx={{ mb: { xs: 3, md: 4 } }}>
                <ButtonBase>
                    <Box
                        display="flex"
                        component="a"
                        href="/"
                        title="Libretto"
                        width={{ xs: 150, md: 180 }}
                        height="auto"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Box component="img" src="/v2-logo.svg" height={1} width={1} />
                    </Box>
                </ButtonBase>
            </Box>

            {/* Text Section */}
            <Box sx={{ mb: theme.spacing(3) }}>
                <Typography
                    sx={{
                        fontFamily: '"Hedvig Letters Serif", serif',
                        fontSize: { xs: '26px', sm: '32px' },        // responsive heading
                        lineHeight: { xs: '32px', sm: '38px' },
                        letterSpacing: '-1.3px',
                        fontWeight: 600,
                        mb: theme.spacing(1),
                        color: 'black',
                    }}
                >
                    Sign in
                </Typography>

                {/* Waitlist / signup copy */}
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: { xs: '12px', sm: '13px' },
                        lineHeight: '20px',
                        opacity: 0.8,
                        color: 'black',
                    }}
                >
                    New here?{' '}
                    <ButtonBase
                        onClick={handleJoinWaitlistClick}
                        sx={{
                            ml: 0.5,
                            marginBottom: 0.35,
                            textDecoration: 'none',
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: '"Inter", sans-serif',
                                fontSize: { xs: '12px', sm: '13px' },
                                lineHeight: '20px',
                                fontWeight: 600,
                                color: 'black',
                                textDecoration: 'underline',
                                textUnderlineOffset: '2px',
                            }}
                        >
                            Join the waitlist
                        </Typography>
                    </ButtonBase>
                </Typography>

                <Typography
                    sx={{
                        mt: 0.75,
                        fontFamily: '"Inter", sans-serif',
                        fontSize: { xs: '11px', sm: '12px' },
                        lineHeight: '18px',
                        opacity: 0.7,
                        color: 'black',
                    }}
                >
                    Libretto is in limited release for existing customers. New signups are added
                    to the waitlist.
                </Typography>

                {/* Google Sign-In Button */}
                <Button
                    onClick={signinWithGoogle}
                    startIcon={<MyGoogleIcon />}
                    variant="contained"
                    fullWidth
                    sx={{
                        ...buttonCommonSx,
                        mt: 3,
                    }}
                >
                    Continue with Google
                </Button>

                <form onSubmit={formik.handleSubmit}>
                    <Box display="flex" alignItems="center" my={theme.spacing(3)}>
                        <Box flex="1" height="1px" bgcolor="divider" />
                        <Typography
                            sx={{
                                mx: theme.spacing(2),
                                fontFamily: '"Inter", sans-serif',
                                fontSize: { xs: '11px', sm: '12px' },
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                opacity: 0.7,
                            }}
                        >
                            Or
                        </Typography>
                        <Box flex="1" height="1px" bgcolor="divider" />
                    </Box>

                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: '12px', sm: '13px' },
                            mb: 0.5,
                        }}
                    >
                        Email
                    </Typography>
                    <TextField
                        variant="outlined"
                        name="email"
                        fullWidth
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        sx={{
                            mb: 2,
                            '& .MuiInputBase-root': {
                                height: 44,
                                fontFamily: '"Inter", sans-serif',
                                fontSize: { xs: '13px', sm: '14px' },
                            },
                        }}
                    />

                    <Grid container item justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontFamily: '"Inter", sans-serif',
                                    fontWeight: 600,
                                    fontSize: { xs: '12px', sm: '13px' },
                                }}
                            >
                                Password
                            </Typography>
                        </Grid>
                        <Grid item>
                            <ButtonBase onClick={handleForgotPasswordClick}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 500,
                                        opacity: 0.6,
                                        fontSize: { xs: '12px', sm: '13px' },
                                        textDecoration: 'underline',
                                        textUnderlineOffset: '2px',
                                    }}
                                >
                                    Forgot password?
                                </Typography>
                            </ButtonBase>
                        </Grid>
                    </Grid>

                    <TextField
                        variant="outlined"
                        name="password"
                        type="password"
                        fullWidth
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        sx={{
                            mt: 0.5,
                            mb: 2,
                            '& .MuiInputBase-root': {
                                height: 44,
                                fontFamily: '"Inter", sans-serif',
                                fontSize: { xs: '13px', sm: '14px' },
                            },
                        }}
                    />

                    <Button
                        variant="contained"
                        type="submit"
                        fullWidth
                        sx={{
                            ...buttonCommonSx,
                            mt: 1,
                        }}
                    >
                        Sign in
                    </Button>
                </form>

                {/* Forgot password dialog */}
                <Dialog
                    open={forgotPasswordMessageOpen}
                    onClose={handleForgotPasswordMessageClose}
                    aria-labelledby="form-dialog-title"
                >
                    <DialogContent
                        sx={{
                            borderRadius: '24px',
                            width: { xs: '100%', md: 400 }, // more flexible on mobile
                            px: { xs: 3, md: 4 },
                            py: { xs: 3, md: 4 },
                        }}
                    >
                        <form onSubmit={resetEmailFormik.handleSubmit}>
                            <Typography
                                sx={{
                                    fontFamily: '"Hedvig Letters Serif", serif',
                                    fontSize: '22px',
                                    lineHeight: '28px',
                                    letterSpacing: '-0.5px',
                                    fontWeight: 500,
                                    mb: theme.spacing(2),
                                    color: 'black',
                                }}
                            >
                                Reset your password
                            </Typography>

                            <TextField
                                variant="outlined"
                                name="email"
                                type="email"
                                fullWidth
                                value={resetEmailFormik.values.email}
                                onChange={resetEmailFormik.handleChange}
                                error={resetEmailFormik.touched.email && Boolean(resetEmailFormik.errors.email)}
                                sx={{
                                    mb: 2,
                                    '& .MuiInputBase-root': {
                                        height: 44,
                                        fontFamily: '"Inter", sans-serif',
                                        fontSize: { xs: '13px', sm: '14px' },
                                    },
                                }}
                            />

                            <Button
                                variant="contained"
                                type="submit"
                                fullWidth
                                disabled={resetEmailSent || resetEmailError}
                                sx={buttonCommonSx}
                            >
                                Send reset link
                            </Button>

                            {(resetEmailSent || resetEmailError) && (
                                <Typography
                                    sx={{
                                        mt: 1.5,
                                        fontFamily: '"Inter", sans-serif',
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        color: 'green',
                                    }}
                                >
                                    If the email is registered, you will receive a password reset link.
                                </Typography>
                            )}
                        </form>
                    </DialogContent>
                </Dialog>
            </Box>

            <Box>{/* extra links if you ever need them */}</Box>
        </Box>
    );
};


export default V2LoginForm;